import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  Table,
  Form,
  Input,
  Button,
  Tag,
  Typography,
  Space,
  DatePicker,
  Select,
  Drawer,
  Descriptions,
} from 'antd';
import { ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import apiClient from '@/services/api';

const { Title } = Typography;

interface AuditRow {
  idx: string;
  timestamp: string;
  actorIdx: string | null;
  actorRole: string | null;
  actorIp: string | null;
  action: string;
  resourceType: string;
  resourceIdx: string | null;
  metadata: any;
  success: boolean;
  errorMessage: string | null;
}

interface Resp {
  Result: string;
  total: number;
  limit: number;
  offset: number;
  logs: AuditRow[];
}

export default function AdminAuditPage() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<{
    action?: string;
    resourceType?: string;
    actor?: string;
    success?: 'true' | 'false' | undefined;
    range?: [Dayjs, Dayjs];
  }>({});
  const [offset, setOffset] = useState(0);
  const [detail, setDetail] = useState<AuditRow | null>(null);

  const params: Record<string, any> = { limit: 50, offset };
  if (filter.action) params.action = filter.action;
  if (filter.resourceType) params.resourceType = filter.resourceType;
  if (filter.actor) params.actor = filter.actor;
  if (filter.success) params.success = filter.success;
  if (filter.range) {
    params.from = filter.range[0].toISOString();
    params.to = filter.range[1].toISOString();
  }

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['admin', 'audit', params],
    queryFn: async () => {
      const r = await apiClient.get<Resp>('/admin/audit-logs', params);
      return r as unknown as Resp;
    },
  });

  return (
    <>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          {t('admin.auditTitle')}
        </Title>
        <Space>
          <Button
            icon={<DownloadOutlined />}
            onClick={async () => {
              // 拉 current filter 的全部（最多 500 筆）一次性匯出
              const exp = await apiClient.get<Resp>('/admin/audit-logs', { ...params, limit: 500, offset: 0 });
              const logs = ((exp as any).data?.logs ?? (exp as any).logs ?? []) as AuditRow[];
              const header = [
                'timestamp',
                'action',
                'resourceType',
                'resourceIdx',
                'actorIdx',
                'actorRole',
                'actorIp',
                'success',
                'errorMessage',
              ];
              const escape = (v: any) => {
                const s = v == null ? '' : String(v);
                return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
              };
              const lines = [header.join(',')];
              for (const r of logs) {
                lines.push([
                  r.timestamp,
                  r.action,
                  r.resourceType,
                  r.resourceIdx ?? '',
                  r.actorIdx ?? '',
                  r.actorRole ?? '',
                  r.actorIp ?? '',
                  r.success,
                  r.errorMessage ?? '',
                ].map(escape).join(','));
              }
              const blob = new Blob([lines.join('\n') + '\n'], { type: 'text/csv;charset=utf-8' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `audit-${dayjs().format('YYYYMMDD-HHmmss')}.csv`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              URL.revokeObjectURL(url);
            }}
          >
            {t('admin.exportCsv')}
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
            {t('admin.refresh')}
          </Button>
        </Space>
      </Space>

      <Card style={{ marginBottom: 16 }}>
        <Form layout="inline" onFinish={() => setOffset(0)}>
          <Form.Item label="Action">
            <Input
              placeholder="e.g. vendor.create"
              allowClear
              onChange={(e) => setFilter((f) => ({ ...f, action: e.target.value || undefined }))}
              style={{ width: 180 }}
            />
          </Form.Item>
          <Form.Item label="Resource type">
            <Input
              placeholder="e.g. Vendor"
              allowClear
              onChange={(e) =>
                setFilter((f) => ({ ...f, resourceType: e.target.value || undefined }))
              }
              style={{ width: 140 }}
            />
          </Form.Item>
          <Form.Item label="Actor idx">
            <Input
              placeholder="user UUID"
              allowClear
              onChange={(e) => setFilter((f) => ({ ...f, actor: e.target.value || undefined }))}
              style={{ width: 260 }}
            />
          </Form.Item>
          <Form.Item label="Success">
            <Select
              allowClear
              placeholder="any"
              style={{ width: 110 }}
              onChange={(v) => setFilter((f) => ({ ...f, success: v }))}
              options={[
                { label: 'true', value: 'true' },
                { label: 'false', value: 'false' },
              ]}
            />
          </Form.Item>
          <Form.Item label="Range">
            <DatePicker.RangePicker
              showTime
              onChange={(v) => setFilter((f) => ({ ...f, range: (v as any) || undefined }))}
            />
          </Form.Item>
          <Form.Item>
            <Button htmlType="submit" type="primary">
              Apply
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Card>
        <Table<AuditRow>
          size="small"
          rowKey="idx"
          loading={isLoading}
          dataSource={data?.logs ?? []}
          onRow={(rec) => ({ onClick: () => setDetail(rec), style: { cursor: 'pointer' } })}
          pagination={{
            current: Math.floor(offset / 50) + 1,
            pageSize: 50,
            total: data?.total ?? 0,
            onChange: (page) => setOffset((page - 1) * 50),
          }}
          columns={[
            {
              title: 'Time',
              dataIndex: 'timestamp',
              key: 'ts',
              width: 175,
              render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm:ss'),
            },
            { title: 'Action', dataIndex: 'action', key: 'action', width: 220 },
            { title: 'Resource', dataIndex: 'resourceType', key: 'rt', width: 130 },
            {
              title: 'Actor',
              key: 'actor',
              render: (_, rec) => (
                <span>
                  {rec.actorRole ? (
                    <Tag color={rec.actorRole.startsWith('system_') ? 'red' : rec.actorRole.startsWith('distributor_') ? 'geekblue' : rec.actorRole.startsWith('vendor_') ? 'green' : 'default'}>
                      {rec.actorRole}
                    </Tag>
                  ) : (
                    <Tag>anon</Tag>
                  )}
                  <code style={{ fontSize: 11 }}>{(rec.actorIdx ?? '').slice(0, 8)}</code>
                </span>
              ),
            },
            {
              title: 'Result',
              dataIndex: 'success',
              key: 'ok',
              width: 90,
              render: (v: boolean) =>
                v ? <Tag color="green">OK</Tag> : <Tag color="red">FAIL</Tag>,
            },
            { title: 'IP', dataIndex: 'actorIp', key: 'ip', width: 130 },
          ]}
        />
      </Card>

      <Drawer
        width={640}
        open={!!detail}
        onClose={() => setDetail(null)}
        title="Audit entry detail"
      >
        {detail && (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Time">
              {dayjs(detail.timestamp).format('YYYY-MM-DD HH:mm:ss.SSS')}
            </Descriptions.Item>
            <Descriptions.Item label="Action">
              <code>{detail.action}</code>
            </Descriptions.Item>
            <Descriptions.Item label="Resource">
              {detail.resourceType} <code>{detail.resourceIdx}</code>
            </Descriptions.Item>
            <Descriptions.Item label="Actor">
              {detail.actorRole} <code>{detail.actorIdx}</code>
            </Descriptions.Item>
            <Descriptions.Item label="IP">{detail.actorIp}</Descriptions.Item>
            <Descriptions.Item label="Success">
              {detail.success ? 'YES' : 'NO'}
            </Descriptions.Item>
            {detail.errorMessage && (
              <Descriptions.Item label="Error">
                <code style={{ color: '#cf1322' }}>{detail.errorMessage}</code>
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Metadata">
              <pre style={{ fontSize: 11, maxHeight: 400, overflow: 'auto' }}>
                {JSON.stringify(detail.metadata, null, 2)}
              </pre>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>
    </>
  );
}
